import { useMemo, useState } from 'react';
import { Combobox, InputBase, useCombobox } from '@mantine/core';
import { useI18n } from '@/shared/i18n';

export interface SearchableSelectOption {
  value: string;
  label: string;
  searchText?: string;
}

interface SearchableSelectProps {
  label?: string;
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  required?: boolean;
  disabled?: boolean;
  testId?: string;
}

export function SearchableSelect({
  label,
  options,
  value,
  onChange,
  placeholder,
  emptyMessage,
  required = false,
  disabled = false,
  testId,
}: SearchableSelectProps) {
  const { t, locale } = useI18n();
  const resolvedPlaceholder = placeholder ?? t('components.searchableSelect.placeholder');
  const resolvedEmptyMessage = emptyMessage ?? t('components.searchableSelect.empty');
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  // null = derive from selected option (avoids setState-in-effect when value/label changes)
  const [draftSearch, setDraftSearch] = useState<string | null>(null);
  const selectedLabel = selectedOption?.label ?? '';
  const search = draftSearch ?? selectedLabel;

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLocaleLowerCase(locale);
    if (!query || search === selectedLabel) {
      return options;
    }
    return options.filter((option) => {
      const haystack = (option.searchText ?? option.label).toLocaleLowerCase(locale);
      return haystack.includes(query) || option.value.toLocaleLowerCase(locale).includes(query);
    });
  }, [locale, options, search, selectedLabel]);

  const displayValue = combobox.dropdownOpened ? search : selectedLabel;

  return (
    <div className="searchable-select" data-testid={testId}>
      <Combobox
        store={combobox}
        withinPortal
        zIndex={400}
        onOptionSubmit={(nextValue) => {
          onChange(nextValue);
          setDraftSearch(null);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            label={label || undefined}
            role="combobox"
            aria-expanded={combobox.dropdownOpened}
            rightSection={<Combobox.Chevron />}
            rightSectionPointerEvents="none"
            value={displayValue}
            onChange={(event) => {
              const next = event.currentTarget.value;
              combobox.updateSelectedOptionIndex();
              setDraftSearch(next);
              combobox.openDropdown();
              if (value) {
                onChange('');
              }
            }}
            onClick={() => {
              if (disabled) return;
              setDraftSearch(selectedLabel || search);
              combobox.openDropdown();
            }}
            onFocus={() => {
              if (disabled) return;
              setDraftSearch(selectedLabel || search);
              combobox.openDropdown();
            }}
            onBlur={() => {
              combobox.closeDropdown();
              setDraftSearch(null);
            }}
            placeholder={resolvedPlaceholder}
            required={required}
            disabled={disabled}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          {/* Fixed max-height so wheel/scroll stays on the list (not the parent Modal ScrollArea). */}
          <Combobox.Options mah={280} style={{ overflowY: 'auto' }}>
            {filteredOptions.length === 0 ? (
              <Combobox.Empty>{resolvedEmptyMessage}</Combobox.Empty>
            ) : (
              filteredOptions.map((option) => (
                <Combobox.Option
                  value={option.value}
                  key={option.value}
                  active={option.value === value}
                  data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                >
                  {option.label}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}
